
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnChanges, Optional, Output, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { CmsSearchBoxComponent, ConverterService, FeaturesConfigModule, I18nModule, LoggerService, PRODUCT_SEARCH_PAGE_NORMALIZER, Product, ProductActions, ProductSearchPage, RoutingService, StateWithProduct, UrlModule, WindowRef, tryNormalizeHttpError } from '@spartacus/core';
import { CmsComponentData, ICON_TYPE, IconModule, LaunchDialogService, MediaModule, OutletModule, SearchBoxComponent, SearchBoxComponentService, SearchBoxModule, SearchResults } from '@spartacus/storefront';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, take, tap } from 'rxjs/operators';
import { GenProductService } from 'src/app/generac/services/product.service';
import { GenSharedModule } from '../../shared/shared.module';
import { ServicePartsSearchButtonComponent } from './service-parts-search-button/service-parts-search-button.component'

@Component({
  selector: 'gen-searchbox',
  templateUrl: './search-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    MediaModule,
    IconModule,
    UrlModule,
    I18nModule,
    OutletModule,
    FeaturesConfigModule,
    GenSharedModule,
    SearchBoxModule,
    ServicePartsSearchButtonComponent,
  ]
})
export class GenSearchBoxComponent extends SearchBoxComponent implements OnChanges {
  private searchSuggestionsByModelNumberSubject = new Subject<string>();
  readonly ICON_TYPE = ICON_TYPE;
  protected subscription = new Subscription();

  @Input() isSpSearch: boolean = false;
  @Input() activeSpSearchCriteria: string | null = null;
  @Output() spSearchProductSelected = new EventEmitter<Product>();

  isServicePartsEnabled: boolean = false;
  selectedSpSearchProduct: Product;
  isMobileSearchBoxShown: boolean;
  redirectToSpTab: boolean = false;
  isMobileSearchOpen: boolean = false;

  override results$: Observable<SearchResults> = this.config$.pipe(
    switchMap((config) => this.searchBoxComponentService.getResults(config)),
    tap(res => {
      this.redirectToSpTab = res.products?.length > 0 && res.products[0].code == this.chosenWord.trimStart().trimEnd() && res.products[0].servicePart;
    })
  );

  @HostListener('window:resize', ['$event'])
  onResizeWindow(event: any) {
    this.isMobileSearchBoxShown = event.target?.innerWidth <= 768;
  }

  get searchPlaceholder(): string {
    if (!this.isSpSearch) {
      return 'searchBox.placeholder';
    }

    return this.activeSpSearchCriteria === 'model'
      ? 'searchBox.spSearchBox.placeholderModelNumber'
      : 'searchBox.spSearchBox.placeholderSerialNumber';
  }

  constructor(
    override searchBoxComponentService: SearchBoxComponentService,
    @Optional()
    override componentData: CmsComponentData<CmsSearchBoxComponent>,
    override winRef: WindowRef,
    override routingService: RoutingService,
    protected productService: GenProductService,
    protected store: Store<StateWithProduct>,
    protected converter: ConverterService,
    protected logger: LoggerService,
    protected launchDialogService: LaunchDialogService,
  ) {
    super(searchBoxComponentService, componentData, winRef, routingService);
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
    if (this.winRef.nativeWindow?.innerWidth) {
      this.isMobileSearchBoxShown = this.winRef.nativeWindow?.innerWidth <= 768;
    }
    this.subscriptions.add(
      this.searchSuggestionsByModelNumberSubject.pipe(
        tap((query) => {
          if (this.activeSpSearchCriteria == 'model' && query !== this.selectedSpSearchProduct?.code?.toLowerCase()) {
            this.selectedSpSearchProduct = null;
            this.spSearchProductSelected.emit(this.selectedSpSearchProduct);
          }
          if (this.activeSpSearchCriteria == 'serial-number') {
            this.spSearchProductSelected.emit({ code: query, name: query } as Product);
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        filter((query) => query && query.length >= 3 && this.activeSpSearchCriteria == 'model'),
        switchMap((query) =>
          this.productService.searchSuggestionsByModelNumber(query).pipe(
            take(1),
            this.converter.pipeable(PRODUCT_SEARCH_PAGE_NORMALIZER),
            tap((data: ProductSearchPage) => {
              this.store.dispatch(
                new ProductActions.SearchProductsSuccess(data, true)
              );
            }),
            catchError((error) => {
              this.store.dispatch(
                new ProductActions.SearchProductsFail(
                  tryNormalizeHttpError(error, this.logger),
                  true
                )
              );
              return of();
            })
          )
        )
      ).subscribe()
    );

    this.subscription.add(
      this.config$.subscribe()
    )
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.winRef.location?.href.indexOf('/search') > -1) {
      this.setChosenWordFromPathname(this.winRef.location?.pathname);
    }
    if (this.isSpSearch) {
      this.updateChosenWord('');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['activeSpSearchCriteria'] && !changes['activeSpSearchCriteria'].firstChange) {
      this.updateChosenWord('');
      this.clearSpSearch(this.searchInputEl, true);
    }
  }

  navigateToSearch(query: string): void {
    const params = {
      queryParams: {
        tab: this.redirectToSpTab ? 1 : undefined,
        query: `${query}:relevance${this.redirectToSpTab ? ':servicePart:true' : ''}`
      }
    };
    this.routingService.go(`/search/${query}`, params);
  }

  setChosenWordFromPathname(pathname: string): void {
    const splittedPathname = pathname?.split('/') || [];
    this.updateChosenWord((decodeURIComponent(decodeURIComponent(splittedPathname?.length > 2 ? splittedPathname[splittedPathname.length - 1] : ''))));
  }

  override search(query: string): void {
    const cleanQuery = query.trim().toLowerCase();
    if (this.isSpSearch) {
      this.searchSuggestionsByModelNumberSubject.next(cleanQuery);
    } else {
      this.searchBoxComponentService.search(query, this.config);
      this.open();
    }
  }

  searchSuggestionsByModelNumber(query: string, options?: { fromFocus?: boolean }): void {
    if (options?.fromFocus) return;
    this.searchSuggestionsByModelNumberSubject.next(query);
  }

  clearSpSearch(el: HTMLInputElement, clearSelectedSpSearchProduct: boolean = true): void {
    if (!this.isSpSearch) return;
    el.value = '';
    this.updateChosenWord(el.value);
    this.searchBoxComponentService.clearResults();
    if (clearSelectedSpSearchProduct) this.selectedSpSearchProduct = null;
    this.spSearchProductSelected.emit(this.selectedSpSearchProduct);
  }

  selectSpSearchProduct(product: Product): void {
    this.selectedSpSearchProduct = product;
    this.spSearchProductSelected.emit(this.selectedSpSearchProduct);
  }

  onFocus(input: HTMLInputElement): void {
    if (this.selectedSpSearchProduct?.code && this.isSpSearch) {
      input.value = this.selectedSpSearchProduct?.code;
      this.searchSuggestionsByModelNumber(this.selectedSpSearchProduct?.code, { fromFocus: true });
    }
  }

  onBlur(input: HTMLInputElement): void {
    if (!this.isSpSearch) return;
    input.value = this.getSpSearchInputValue(input.value);
  }

  getSpSearchInputValue(currentValue: string): string {
    return this.activeSpSearchCriteria === 'model' ? (this.selectedSpSearchProduct?.code && this.selectedSpSearchProduct?.code + (this.selectedSpSearchProduct?.name ? (' - ' + this.selectedSpSearchProduct?.name) : currentValue ?? ' ')) || currentValue : '';
  }

  onInput(value: string): void {
    this.search(value);
    this.updateChosenWord(value);
  }

  override onEnter(value: string): void {
    if(!value) return;

    this.close(true);
    this.updateChosenWord(value);
    this.navigateToSearch(value);
  }

  onClickSearch(value: string): void {
    if(!value) return;
    
    this.close(true);
    this.updateChosenWord(value);
    this.navigateToSearch(value);
    this.closeMobileSearch(); 
  }

  onClickCancel(searchInput: HTMLInputElement): void {
    this.close(true);
    this.closeMobileSearch(); 
    searchInput.value = null;
  }

  onClickFindParts(searchInput: HTMLInputElement): void {
    this.close(true);
    this.closeMobileSearch(); 
    this.openServicePartsSearchDialog();
    searchInput.value = null;
  }

  openMobileSearch(): void {
    if (this.winRef.nativeWindow?.innerWidth <= 768) {
      this.isMobileSearchOpen = true;
    }
  }

  closeMobileSearch(): void {
    this.isMobileSearchOpen = false;
  }
  
  openServicePartsSearchDialog(): void {
    const servicePartsSearchDialog = this.launchDialogService.openDialog(
      'SERVICE_PARTS_SEARCH',
      undefined,
      undefined,
      {}
    );

    if (servicePartsSearchDialog) {
      servicePartsSearchDialog.pipe(take(1)).subscribe();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions?.unsubscribe();
  }
}